import React, { forwardRef, HTMLProps, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { Button, ButtonProps, Typography } from '@mui/material';
import cx from 'classnames';

import styles from './Box.module.scss';

export type BoxProps = {
  title?: ReactNode;
  className?: string;
  onClick?: (event: any) => void;
  borderRadius?: number;
  rightView?: ReactElement;
  titleWrapper?: (children: ReactNode) => ReactNode;
  actionTitle?: string;
  actionProps?: ButtonProps;
  FooterComponent?: ReactNode;
  rightAction?: {
    title: string;
    onClick: () => void;
  };
};

function Header({ title, rightAction, rightView, titleWrapper }: BoxProps) {
  if (!title && !rightAction) {
    return null;
  }

  const TitleComponent = (() => {
    if (!title) return <div />;
    const titleComponent = <Typography variant="subtitle1">{title}</Typography>;
    if (titleWrapper) {
      return titleWrapper(titleComponent);
    }
    return titleComponent;
  })();

  return (
    <div className={cx(styles.header, 'box-header')}>
      {TitleComponent}
      {rightView}
      {!!rightAction ? (
        <Button onClick={rightAction.onClick} variant="text" color="primary">
          {rightAction.title}
        </Button>
      ) : null}
    </div>
  );
}

function Footer({
  actionProps,
  actionTitle,
  FooterComponent,
}: Pick<BoxProps, 'actionProps' | 'actionTitle' | 'FooterComponent'>) {
  if (!actionTitle && !FooterComponent) {
    return null;
  }
  return (
    <div className={styles.footer}>
      {FooterComponent || (
        <div className={styles.footerActionContainer}>
          <div />
          <Button variant="text" color="primary" {...actionProps}>
            {actionTitle}
          </Button>
        </div>
      )}
    </div>
  );
}

export const Box = forwardRef<
  HTMLDivElement,
  PropsWithChildren<BoxProps & HTMLProps<HTMLDivElement>>
>((props, ref) => {
  const {
    title,
    titleWrapper,
    children,
    className,
    onClick,
    rightAction,
    rightView,
    borderRadius,
    actionTitle,
    actionProps,
    FooterComponent,
    ...rest
  } = props;
  return (
    <div
      ref={ref}
      onClick={onClick}
      {...(borderRadius ? { borderRadius } : undefined)}
      className={[
        styles.box,
        !!onClick ? styles.pointer : '',
        styles.box_no_shadow,
        className,
      ].join(' ')}
      {...rest}
    >
      <Header
        title={title}
        rightAction={rightAction}
        rightView={rightView}
        titleWrapper={titleWrapper}
      />
      {children}
      <Footer
        actionProps={actionProps}
        actionTitle={actionTitle}
        FooterComponent={FooterComponent}
      />
    </div>
  );
});
